import { useState } from "react";
import { Play } from "lucide-react";

// youtube.com/watch?v=ID, youtu.be/ID, youtube.com/shorts/ID, youtube.com/embed/ID, m.youtube.com/…
const YOUTUBE_URL =
  /^https?:\/\/(?:www\.|m\.)?(?:youtube\.com\/(?:watch\?(?:[^#\s]*&)?v=|shorts\/|embed\/|live\/)|youtu\.be\/)([\w-]{11})\S*$/i;

export function youtubeFromUrl(url?: string) {
  const m = url?.trim().match(YOUTUBE_URL);
  return m ? { id: m[1], vertical: /\/shorts\//i.test(url!) } : null;
}

// A Portable Text paragraph that is nothing but a YouTube link (pasted on its own line in Studio)
export function youtubeFromBlock(block: { children?: { text?: string }[] }) {
  return youtubeFromUrl(block.children?.map((c) => c.text ?? "").join(""));
}

// Click-to-load: shows the thumbnail until played, so a post with videos stays light.
// The player comes from youtube-nocookie.com (no cookies until playback).
export function YouTubeEmbed({ id, vertical = false, className = "my-8" }: { id: string; vertical?: boolean; className?: string }) {
  const [playing, setPlaying] = useState(false);
  const frame = `shimmer ${className} overflow-hidden rounded-2xl border border-border ${
    vertical ? "aspect-[9/16] w-full max-w-[20rem]" : "aspect-video w-full"
  }`;

  if (playing) {
    return (
      <div className={frame}>
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0&playsinline=1`}
          title="YouTube video"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          // YouTube's player needs a referrer; this overrides the page-wide no-referrer that X embeds set
          referrerPolicy="strict-origin-when-cross-origin"
          className="relative z-[1] h-full w-full"
        />
      </div>
    );
  }

  return (
    <button type="button" onClick={() => setPlaying(true)} aria-label="Play video" className={`group relative block ${frame}`}>
      <img
        src={`https://i.ytimg.com/vi/${id}/hqdefault.jpg`}
        alt=""
        loading="lazy"
        decoding="async"
        className="relative z-[1] h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03]"
      />
      <span className="absolute inset-0 z-[2] flex items-center justify-center bg-black/10 transition-colors group-hover:bg-black/20">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-black/70 text-white shadow-lg backdrop-blur-sm transition-transform duration-200 group-hover:scale-110">
          <Play className="ml-0.5 h-6 w-6 fill-current" />
        </span>
      </span>
    </button>
  );
}
